import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Clock3, Film, Image as ImageIcon, LoaderCircle, RefreshCw, Trash2, XCircle } from 'lucide-react';
import { api } from '../lib/api.js';

const ACTIVE = new Set(['queued', 'pending', 'processing', 'running', 'generating']);
const FAILED = new Set(['failed', 'cancelled', 'canceled', 'review']);
const RETRYABLE = new Set(['failed', 'cancelled', 'canceled']);

function taskDate(task) {
  const value = task.completedAt || task.updatedAt || task.createdAt;
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

function elapsed(task) {
  const start = new Date(task.createdAt || Date.now()).getTime();
  const end = new Date(task.completedAt || task.updatedAt || Date.now()).getTime();
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function normalize(tasks, videoTasks) {
  return [...(tasks || []), ...(videoTasks || [])].map((task) => ({
    ...task,
    kind: task.type === 'video' || task.videoUrl || task.duration ? 'video' : 'image',
    state: task.status || task.state || 'queued',
    date: taskDate(task),
  })).sort((a, b) => b.date - a.date);
}

export default function CanvasTaskMonitor({ open, tasks, videoTasks, onClose, onRefresh, onNotify, onRetry }) {
  const [tab, setTab] = useState('active');
  const [working, setWorking] = useState('');
  const all = useMemo(() => normalize(tasks, videoTasks), [tasks, videoTasks]);
  const filtered = all.filter((task) => tab === 'active' ? ACTIVE.has(task.state) : tab === 'success' ? task.state === 'completed' : FAILED.has(task.state));
  const stats = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayTasks = all.filter((task) => task.date >= today);
    const success = todayTasks.filter((task) => task.state === 'completed').length;
    const failed = todayTasks.filter((task) => FAILED.has(task.state)).length;
    return { total: todayTasks.length, success, failed, rate: todayTasks.length ? Math.round(success / todayTasks.length * 100) : 0 };
  }, [all]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setInterval(() => void onRefresh?.(), 5000);
    return () => window.clearInterval(timer);
  }, [onRefresh, open]);

  if (!open) return null;
  const remove = async (task) => {
    setWorking(task.id);
    try {
      if (task.state === 'review') throw new Error('该任务正在人工核对，不能从用户端删除或重复提交。');
      if (task.kind === 'video') {
        if (ACTIVE.has(task.state)) {
          if (!task.canCancel) throw new Error('视频已经提交到上游，不能取消；请等待完成或进入人工核对。');
          await api.cancelVideoTask(task.id);
        } else await api.deleteVideoTask(task.id);
      } else await api.deleteTask(task.id);
      await onRefresh?.();
      onNotify?.(task.kind === 'video' && ACTIVE.has(task.state) ? '视频任务已取消，积分已退回' : '任务记录已删除');
    } catch (error) { onNotify?.(error.message, 'error'); }
    finally { setWorking(''); }
  };
  const retry = async (task) => {
    setWorking(task.id);
    try { await onRetry?.(task); await onRefresh?.(); onNotify?.('已重新提交任务'); }
    catch (error) { onNotify?.(error.message, 'error'); }
    finally { setWorking(''); }
  };

  return <aside className="canvas-task-monitor" aria-label="生成任务监控">
    <header><div><strong>生成任务</strong><small>实时状态与历史记录</small></div><button type="button" onClick={onClose} title="关闭"><XCircle size={18} /></button></header>
    <div className="task-monitor-stats"><span><b>{stats.total}</b><small>今日总数</small></span><span><b>{stats.success}</b><small>成功</small></span><span><b>{stats.failed}</b><small>失败</small></span><span><b>{stats.rate}%</b><small>成功率</small></span></div>
    <nav className="task-monitor-tabs"><button type="button" className={tab === 'active' ? 'is-active' : ''} onClick={() => setTab('active')}>进行中 <b>{all.filter((task) => ACTIVE.has(task.state)).length}</b></button><button type="button" className={tab === 'success' ? 'is-active' : ''} onClick={() => setTab('success')}>已成功 <b>{all.filter((task) => task.state === 'completed').length}</b></button><button type="button" className={tab === 'failed' ? 'is-active' : ''} onClick={() => setTab('failed')}>已失败 <b>{all.filter((task) => FAILED.has(task.state)).length}</b></button></nav>
    <div className="task-monitor-list">
      {filtered.map((task) => <article key={`${task.kind}:${task.id}`} className={`task-monitor-item is-${task.state}`}>
        <div className="task-monitor-thumb">{task.kind === 'video' ? (task.coverUrl ? <img src={task.coverUrl} alt="" /> : <Film size={19} />) : (task.images?.[0] || task.imageUrls?.[0] ? <img src={task.images?.[0] || task.imageUrls?.[0]} alt="" /> : <ImageIcon size={19} />)}</div>
        <div className="task-monitor-copy"><strong>{task.prompt || '未命名任务'}</strong><small>{task.kind === 'video' ? '生视频' : '生图'} · {task.modelName || task.model || '-'} · {task.date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}</small><span>{ACTIVE.has(task.state) ? <><LoaderCircle className="spin" size={12} />{task.progress || 0}% · {elapsed(task)}</> : task.filesExpired ? <><Clock3 size={12} />结果已过期</> : task.state === 'completed' ? <><CheckCircle2 size={12} />完成 · {elapsed(task)}</> : <><XCircle size={12} />{task.error || task.error_message || task.message || '生成失败'}</>}</span></div>
        <div className="task-monitor-actions">{RETRYABLE.has(task.state) && <button type="button" onClick={() => void retry(task)} disabled={working === task.id} title="重试"><RefreshCw size={14} /></button>}<button type="button" onClick={() => void remove(task)} disabled={working === task.id || task.state === 'review' || (ACTIVE.has(task.state) && task.kind === 'video' && !task.canCancel)} title={task.state === 'review' ? '等待管理员核对' : ACTIVE.has(task.state) ? (task.kind === 'video' && task.canCancel ? '取消并退款' : '已提交，不能取消') : '删除'}><Trash2 size={14} /></button></div>
      </article>)}
      {!filtered.length && <div className="task-monitor-empty"><Clock3 size={22} /><span>这里暂时没有任务</span></div>}
    </div>
    <footer><button type="button" onClick={() => { const deletable = all.filter((task) => !ACTIVE.has(task.state)); void Promise.allSettled(deletable.map((task) => task.kind === 'video' ? api.deleteVideoTask(task.id) : api.deleteTask(task.id))).then(() => onRefresh?.()).catch((error) => onNotify?.(error.message, 'error')); }}><Trash2 size={14} />清空可删除记录</button></footer>
  </aside>;
}
